import { PlacesApiError } from '../../erros/places_service_error';
import {
  GooglePlacesApiAdapterOptions,
  IPlacesApiAdapter,
  PlaceDetails,
  PlacesApiAdditionalOptions,
  PlaceSuggestion,
} from '../../types';

export class GooglePlacesApiAdapter implements IPlacesApiAdapter {
  private readonly Place: typeof google.maps.places.Place;

  private readonly Autocomplete: typeof google.maps.places.AutocompleteSuggestion;

  private readonly fields = ['formattedAddress', 'addressComponents'];

  private readonly language: string;

  private readonly countries: string[];

  private readonly region: string;

  private static get statusList() {
    return {
      PERMISSION_DENIED: 'PERMISSION_DENIED',
      ...google.maps.places.PlacesServiceStatus,
    };
  }

  constructor({
    language = 'pt-BR',
    countries = ['BR'],
    region = 'BR',
  }: GooglePlacesApiAdapterOptions = {}) {
    GooglePlacesApiAdapter.checkDependencies();

    this.Place = google.maps.places.Place;
    this.Autocomplete = google.maps.places.AutocompleteSuggestion;
    this.language = language;
    this.countries = countries;
    this.region = region;
  }

  async suggestPlaces(input: string): Promise<PlaceSuggestion[]> {
    try {
      const data = await this.Autocomplete.fetchAutocompleteSuggestions({
        input,
        language: this.language,
        region: this.region,
        includedRegionCodes: this.countries,
      });

      return data.suggestions
        .filter((suggestion) => suggestion.placePrediction != null)
        .map(({ placePrediction: prediction }) => ({
          placeId: prediction!.placeId,
          description: prediction!.text?.toString() || '',
          mainText: prediction!.mainText?.toString() || '',
          secondaryText: prediction!.secondaryText?.toString() || '',
        }));
    } catch (error) {
      throw GooglePlacesApiAdapter.mapServiceError(error);
    }
  }

  async searchPlaces(input: string, options?: PlacesApiAdditionalOptions): Promise<PlaceDetails[]> {
    try {
      const data = await this.Place.searchByText({
        fields: this.fields.concat(options?.fields || []),
        language: this.language,
        textQuery: input,
        region: this.region,
      });

      return data.places.map((place) => GooglePlacesApiAdapter.formatPlaceDetails(place));
    } catch (error) {
      throw GooglePlacesApiAdapter.mapServiceError(error);
    }
  }

  async getPlaceDetails(
    placeId: string,
    options?: PlacesApiAdditionalOptions,
  ): Promise<PlaceDetails> {
    try {
      const place = new this.Place({
        id: placeId,
        requestedLanguage: this.language,
        requestedRegion: this.region,
      });
      const data = await place.fetchFields({ fields: this.fields.concat(options?.fields || []) });

      return GooglePlacesApiAdapter.formatPlaceDetails(data.place);
    } catch (error) {
      throw GooglePlacesApiAdapter.mapServiceError(error);
    }
  }

  private static checkDependencies(): void {
    if (!window.google?.maps?.places?.Place) {
      throw new Error('Google Maps Place is not available.');
    }

    if (!window.google?.maps?.places?.AutocompleteSuggestion) {
      throw new Error('Google Maps AutocompleteSuggestion is not available.');
    }
  }

  private static getStatusData(status: string): {
    type: string;
    message: string;
    feedback: string;
  } {
    const map: Record<string, { type: string; message: string; feedback: string }> = {
      [GooglePlacesApiAdapter.statusList.OVER_QUERY_LIMIT]: {
        type: 'warning',
        message: 'Google Places Service: Request limit exceeded.',
        feedback: 'Limite de requisições excedido, tente novamente mais tarde.',
      },
      [GooglePlacesApiAdapter.statusList.REQUEST_DENIED]: {
        type: 'error',
        message: 'Google Places Service: Access denied.',
        feedback: 'A página não tem permissão para realizar essa ação.',
      },
      [GooglePlacesApiAdapter.statusList.INVALID_REQUEST]: {
        type: 'error',
        message: 'Google Places Service: Invalid data.',
        feedback: 'Verifique se os dados estão corretos e tente novamente.',
      },
      [GooglePlacesApiAdapter.statusList.UNKNOWN_ERROR]: {
        type: 'error',
        message: 'Google Places Service: An unexpected error occurred.',
        feedback: 'Ocorreu um erro inesperado. Tente novamente mais tarde.',
      },
      [GooglePlacesApiAdapter.statusList.PERMISSION_DENIED]: {
        type: 'error',
        message: 'Google Places Service: Permission denied.',
        feedback: 'Permissão negada para acessar o serviço do google.',
      },
    };

    return (
      map[status] ||
      map[GooglePlacesApiAdapter.statusList.UNKNOWN_ERROR] || {
        type: 'error',
        message: 'Unknown error',
        feedback: 'An unknown error occurred',
      }
    );
  }

  private static mapServiceError(err: any): PlacesApiError {
    const { code, message } = err;
    const { feedback, type } = GooglePlacesApiAdapter.getStatusData(code);

    return new PlacesApiError(`Google Places Service: ${message}`, {
      type,
      feedback,
      code,
      origin: err,
    });
  }

  private static formatPlaceDetails(place: google.maps.places.Place): PlaceDetails {
    return {
      placeId: place.id || '',
      name: place.displayName || '',
      formattedAddress: place.formattedAddress || '',
      location: GooglePlacesApiAdapter.formatPlaceLocation(place),
      addressComponents: (place.addressComponents || []).map((component) => ({
        longName: component.longText || '',
        shortName: component.shortText || '',
        types: component.types || [],
      })),
    };
  }

  private static formatPlaceLocation(place: google.maps.places.Place) {
    if (!place.location) return null;

    return {
      latitude: place.location.lat(),
      longitude: place.location.lng(),
    };
  }
}
